/**
 * The E02.7 managed-upload lifecycle: capabilities, recovery read, presign, the
 * object-store POST, confirm, finalize and abort.
 *
 * Part of the Console's API client: import from `engine/api.js`, which re-exports this module.
 */

import {
  BASE,
  headers,
  fetchJson,
  fetchJsonCoded,
} from "./apiTransport";

// ── E02.7 Managed Upload lifecycle clients ─────────────────────────────────
// The legacy E02.1 files[]-shaped presign/confirm clients (sha256Hex via
// crypto.subtle, presignInputs, uploadToPresignedUrl PUT, confirmInputs) are
// RETIRED: the backend now 410s that request shape. The canonical flow is
// session/attempt identity (create -> presign -> POST -> confirm -> finalize),
// and hashing is the streaming SHA-256 in engine/sha256.js (crypto.subtle has no
// streaming digest and FR17 forbids whole-file arrayBuffer()).

/**
 * GET /upload-capabilities (E02.7 FR2) — authoritative, cacheable discovery of
 * the managed-upload modes, safe-filename grammar, hash algorithm, limits
 * (max files / per-file / total bytes), session/target TTLs, and transport. The
 * Console consumes these limits instead of hard-coding them; when managed upload
 * is unavailable (dark-launch flag off) `managed_upload_available` is false and
 * `managed_upload` is absent from `modes`, so the Console fails closed. No secrets.
 */
export async function getUploadCapabilities() {
  return fetchJson(`${BASE}/upload-capabilities`, { headers: headers() });
}

/**
 * GET /jobs/{jobId}/inputs (E02.7 FR15) — the tenant-owned session / logical
 * file / current-attempt recovery read model. Never returns a presigned URL,
 * policy, signature, or credential. Used to reconstruct upload state after a
 * refresh: validated files stay validated, a pending attempt can be confirmed,
 * and a file whose local bytes are gone needs re-selection (matching name/size/
 * sha256). A cross-tenant / unknown job is a non-disclosing 404.
 */
export async function getJobInputs(jobId) {
  return fetchJson(`${BASE}/jobs/${encodeURIComponent(jobId)}/inputs`, { headers: headers() });
}

/**
 * POST /jobs/{jobId}/inputs:presign (E02.7 FR5) — create or return the current
 * immutable upload attempt for ONE artifact and get a transport-explicit
 * presigned POST target. Body is attempt identity only ({ session_id,
 * artifact_id }); the `Idempotency-Key` header is REQUIRED (a replay before
 * expiry returns the same attempt). Returns { method:"POST", url, form_fields,
 * required_headers, expires_at, max_bytes, attempt_id, attempt_no, ... }. The
 * signed material exists only in this response — never persist/log it.
 */
export async function presignInput(jobId, { sessionId, artifactId }, idempotencyKey) {
  const h = headers();
  if (idempotencyKey) h["Idempotency-Key"] = idempotencyKey;
  return fetchJsonCoded(`${BASE}/jobs/${encodeURIComponent(jobId)}/inputs:presign`, {
    method: "POST",
    headers: h,
    body: JSON.stringify({ session_id: sessionId, artifact_id: artifactId }),
  });
}

/**
 * Upload one file's bytes to its presigned object-store POST target (E02.7 FR18).
 *
 * Uses XMLHttpRequest — the documented exception to the "all network via fetch"
 * rule — for real upload-progress events (fetch cannot report request-body
 * progress). The presigned POST is a browser->object-store request:
 *   - build a FormData, append EVERY `form_fields` entry VERBATIM (order
 *     preserved) FIRST, then append the file LAST as fd.append("file", file,
 *     destinationName) — S3 POST policy requires the file field to be last;
 *   - send any `required_headers` verbatim, but NEVER an Authorization header
 *     (the POST policy is the auth) and NEVER set request Content-Type (the
 *     browser must set the multipart boundary itself).
 * Resolves on a 2xx (reporting fraction 1), rejects on a non-2xx / network error,
 * and is abortable via `opts.signal` (rejects with an AbortError).
 *
 * @param {object} presign  the PresignInputResponse
 * @param {File|Blob} file
 * @param {object} [opts]
 * @param {string} [opts.filename]  multipart filename (the destination_name)
 * @param {(fraction:number)=>void} [opts.onProgress]  0..1 best-effort
 * @param {AbortSignal} [opts.signal]
 */
export function postToObjectStore(presign, file, { filename, onProgress, signal } = {}) {
  return new Promise((resolve, reject) => {
    const abortErr = () => {
      const e = new Error("upload was cancelled");
      e.name = "AbortError";
      return e;
    };
    if (signal?.aborted) {
      reject(abortErr());
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open(presign.method || "POST", presign.url);
    // Required signed headers verbatim (empty for a POST policy). Deliberately
    // NO Authorization header, and NO manual Content-Type (the browser sets the
    // multipart/form-data boundary).
    const requiredHeaders = presign.required_headers || {};
    for (const [k, v] of Object.entries(requiredHeaders)) xhr.setRequestHeader(k, v);
    if (typeof onProgress === "function" && xhr.upload) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && e.total > 0) onProgress(e.loaded / e.total);
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        if (typeof onProgress === "function") onProgress(1);
        resolve({ status: xhr.status });
      } else {
        reject(new Error(`Upload failed: HTTP ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error("Upload failed: network error"));
    xhr.onabort = () => reject(abortErr());
    if (signal) {
      signal.addEventListener("abort", () => {
        try {
          xhr.abort();
        } catch {
          /* already settled */
        }
      });
    }
    const fd = new FormData();
    const fields = presign.form_fields || {};
    for (const [k, v] of Object.entries(fields)) fd.append(k, v);
    // File LAST (S3 POST-policy requirement); the multipart filename is the
    // destination name, not the local filename.
    fd.append("file", file, filename || (file && file.name) || "file");
    xhr.send(fd);
  });
}

/**
 * POST /jobs/{jobId}/inputs:confirm (E02.7 FR6) — server-authoritative validation
 * of ONE attempt. Body is attempt identity only ({ session_id, attempt_id }); any
 * client size/hash/version field is a 422 (the server HEADs the object and records
 * observed size/checksum/version). Returns HTTP 200 EVEN ON A VALIDATION FAILURE —
 * the outcome rides in the body ({ artifact_status, failure_code, failure_reason,
 * observed_*, session_ready, ready_at }). A 410 means the upload target expired.
 */
export async function confirmInput(jobId, { sessionId, attemptId }) {
  return fetchJsonCoded(`${BASE}/jobs/${encodeURIComponent(jobId)}/inputs:confirm`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ session_id: sessionId, attempt_id: attemptId }),
  });
}

/**
 * POST /jobs/{jobId}/inputs:finalize (E02.7 FR9) — the ONLY operation that routes
 * a ready managed-upload job. Body is session identity only ({ session_id }).
 * Returns the full JobResponse (status queued|vetoed, venue, non-empty Adam/Eve
 * reasoning once a decision exists) plus a sealed `upload_session`. Idempotent: a
 * replay returns the same result. A 429 is `queue_full` (retryable — the session
 * stays ready), NOT a rate limit.
 */
export async function finalizeInputs(jobId, sessionId) {
  return fetchJsonCoded(`${BASE}/jobs/${encodeURIComponent(jobId)}/inputs:finalize`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ session_id: sessionId }),
  });
}

/**
 * POST /jobs/{jobId}/inputs:abort (E02.7 FR14) — idempotently abort an open
 * session and cancel the waiting job. Body is session identity only
 * ({ session_id }). Returns the AUTHORITATIVE terminal state ({ job_status:
 * "cancelled", input_failure_code:"input_upload_aborted", upload_session:{ status:
 * "aborted", ... } }); an unconfirmed network response is NOT proof of
 * cancellation, so the Console only shows "Cancelled" on this 200.
 */
export async function abortInputs(jobId, sessionId) {
  return fetchJsonCoded(`${BASE}/jobs/${encodeURIComponent(jobId)}/inputs:abort`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ session_id: sessionId }),
  });
}
